<top-bar-filters
  [reactiveForm]="filtersForm"
  [formLayoutConfig]="filtersLayoutConfig"
  [formControlsConfigMap]="filtersConfigMap"
  [valuesMap]="valuesMap"
  [nonRemovableSectionIds]="['dateRange']"
  (updateValuesMap)="updateValuesMap($event)"
  (onApply)="applyFilters()"
  (onClear)="clearFilters()"
>
</top-bar-filters>

@if (error) {
  <hub-alert class="sla__error" alertType="danger">{{ error }}</hub-alert>
}

@if (loading) {
  <div class="loading__message d-flex align-items-center">
    <mat-spinner [diameter]="20"></mat-spinner>
    <span class="loading__message-text ml-3">Loading real-time data, please wait…</span>
  </div>
}

@if (summary && !loading) {
  @if (noData) {
    <div class="sla__no-data">
      <mat-icon class="sla__no-data-icon">search_off</mat-icon>
      <p class="sla__no-data-title">No data available for the selected filters.</p>
      <p class="sla__no-data-subtitle">Try adjusting or clearing the filters to see results.</p>
    </div>
  } @else {
    <div class="sla__kpi-row">
      <div class="sla__kpi-card">
        <span class="sla__kpi-label">Total Number of Tickets</span>
        <span class="sla__kpi-value sla__kpi-value--brand">{{ summary.totalTickets | number }}</span>
      </div>
      <div class="sla__kpi-card">
        <span class="sla__kpi-label">Open Tickets</span>
        <span class="sla__kpi-value">{{ summary.openTickets | number }}</span>
      </div>
      <div class="sla__kpi-card">
        <span class="sla__kpi-label">Closed Tickets</span>
        <span class="sla__kpi-value">{{ summary.closedTickets | number }}</span>
      </div>
      <div class="sla__kpi-card">
        <span class="sla__kpi-label">Tickets in Waiting</span>
        <span class="sla__kpi-value">{{ summary.waitingTickets | number }}</span>
      </div>
      <div class="sla__kpi-card">
        <span class="sla__kpi-label">Oldest Ticket</span>
        <span class="sla__kpi-value">{{ summary.oldestTicketDays | number }}</span>
        <span class="sla__kpi-unit">Days</span>
      </div>
      <div class="sla__kpi-card">
        <span class="sla__kpi-label">Avg. Response Time</span>
        <span class="sla__kpi-value">{{ formatDuration(summary.avgResponseTimeMins) }}</span>
      </div>
    </div>

    <div class="sla__accordion-list">
      @for (row of summary.byPriority; track row.priority) {
        <div class="sla__accordion-item">
          <button
            class="sla__accordion-header"
            (click)="togglePriority(row.priority, row)"
            [attr.aria-expanded]="isPriorityExpanded(row.priority)"
          >
            <div class="sla__priority-label">
              <span class="sla__priority-text">Priority</span>
              <span class="sla__priority-badge">{{ row.priority.replace('Priority ', '') }}</span>
            </div>

            <div class="sla__stat-cells">
              <div class="sla__stat-cell">
                <span class="sla__stat-label">Tickets</span>
                <span class="sla__stat-value">{{ row.ticketCount | number }}</span>
              </div>
              <div class="sla__stat-cell">
                <span class="sla__stat-label">Percent of Total</span>
                <span class="sla__stat-value">{{ row.percentOfTotal }}%</span>
              </div>
              <div
                class="sla__stat-cell"
                [ngClass]="statCellClass(responseTimeColor(row.avgResponseTimeMins, effectiveRespondTarget(row)))"
              >
                <span class="sla__stat-label">Avg. Response Time</span>
                <div class="sla__stat-value-row">
                  <span
                    class="sla__stat-value"
                    [ngClass]="responseTimeColor(row.avgResponseTimeMins, effectiveRespondTarget(row))"
                  >
                    {{ formatDuration(row.avgResponseTimeMins) }}
                  </span>
                  @if (
                    effectiveRespondTarget(row) !== null &&
                    responseTimeColor(row.avgResponseTimeMins, effectiveRespondTarget(row))
                  ) {
                    <mat-icon
                      class="sla__stat-icon"
                      [ngClass]="responseTimeColor(row.avgResponseTimeMins, effectiveRespondTarget(row))"
                    >
                      {{ slaStatusIcon(responseTimeColor(row.avgResponseTimeMins, effectiveRespondTarget(row))) }}
                    </mat-icon>
                  }
                </div>
                @if (effectiveRespondTarget(row) !== null) {
                  <span class="sla__stat-target">Target ({{ formatDuration(effectiveRespondTarget(row)) }})</span>
                }
              </div>
              <div
                class="sla__stat-cell"
                [ngClass]="statCellClass(resolveTimeColor(row.avgResolveTimeMins, effectiveResolveTarget(row)))"
              >
                <span class="sla__stat-label">Avg. Resolve Time</span>
                <div class="sla__stat-value-row">
                  <span
                    class="sla__stat-value"
                    [ngClass]="resolveTimeColor(row.avgResolveTimeMins, effectiveResolveTarget(row))"
                  >
                    {{ formatDuration(row.avgResolveTimeMins) }}
                  </span>
                  @if (
                    effectiveResolveTarget(row) !== null &&
                    resolveTimeColor(row.avgResolveTimeMins, effectiveResolveTarget(row))
                  ) {
                    <mat-icon
                      class="sla__stat-icon"
                      [ngClass]="resolveTimeColor(row.avgResolveTimeMins, effectiveResolveTarget(row))"
                    >
                      {{ slaStatusIcon(resolveTimeColor(row.avgResolveTimeMins, effectiveResolveTarget(row))) }}
                    </mat-icon>
                  }
                </div>
                @if (effectiveResolveTarget(row) !== null) {
                  <span class="sla__stat-target">Target ({{ formatDuration(effectiveResolveTarget(row)) }})</span>
                }
              </div>
              <div
                class="sla__stat-cell"
                [ngClass]="row.slaMissCount > 0 ? 'sla__stat-cell--red' : 'sla__stat-cell--green'"
              >
                <span class="sla__stat-label">SLA Miss</span>
                <div class="sla__stat-value-row">
                  <span
                    class="sla__stat-value"
                    [ngClass]="row.slaMissCount > 0 ? 'sla__value--red' : 'sla__value--green'"
                    >{{ row.slaMissCount | number }}</span
                  >
                  @if (row.slaMissCount > 0) {
                    <mat-icon class="sla__stat-icon sla__value--red">cancel</mat-icon>
                  }
                </div>
                <span class="sla__stat-target">Target (0)</span>
              </div>
            </div>

            <mat-icon class="sla__chevron" [class.sla__chevron--open]="isPriorityExpanded(row.priority)">
              expand_more
            </mat-icon>
          </button>

          @if (isPriorityExpanded(row.priority)) {
            @if (row.byMonth?.length) {
              <div class="sla__chart-section">
                <span class="sla__chart-title">Tickets by Month</span>
                <div class="sla__chart-wrapper">
                  <canvas
                    baseChart
                    [type]="'bar'"
                    [data]="expandedChartData"
                    [options]="barOptions"
                    [plugins]="slaBarChartPlugins"
                  ></canvas>
                </div>
              </div>
            }
            <div class="sla__ticket-table-wrapper">
              @if (expandedTicketsLoading) {
                <div class="sla__tickets-loading d-flex justify-content-center align-items-center">
                  <mat-spinner [diameter]="28"></mat-spinner>
                </div>
              } @else {
                <table
                  mat-table
                  [dataSource]="expandedTickets?.data ?? []"
                  class="udt-table w-100"
                  matSort
                  matSortDisableClear
                  [matSortActive]="expandedSortBy"
                  [matSortDirection]="expandedSortDir"
                  (matSortChange)="onExpandedSortChange($event)"
                >
                  <ng-container matColumnDef="id">
                    <th mat-header-cell *matHeaderCellDef mat-sort-header>Ticket #</th>
                    <td mat-cell *matCellDef="let t">
                      <a
                        class="sla__ticket-link"
                        [routerLink]="['/secured/managed-services/tickets', t.id]"
                        [queryParams]="{ returnUrl: '/secured/managed-services/ticket-analysis/sla' }"
                        (click)="saveNavigationState()"
                        >{{ t.id }}</a
                      >
                    </td>
                  </ng-container>
                  <ng-container matColumnDef="dateEntered">
                    <th mat-header-cell *matHeaderCellDef mat-sort-header>Date Entered</th>
                    <td mat-cell *matCellDef="let t">{{ t.dateEntered | hubDate }}</td>
                  </ng-container>
                  <ng-container matColumnDef="closedDate">
                    <th mat-header-cell *matHeaderCellDef mat-sort-header>Date Closed</th>
                    <td mat-cell *matCellDef="let t">{{ t.closedDate ? (t.closedDate | hubDate) : '—' }}</td>
                  </ng-container>
                  <ng-container matColumnDef="status">
                    <th mat-header-cell *matHeaderCellDef mat-sort-header>Status</th>
                    <td mat-cell *matCellDef="let t">{{ t.status ?? '—' }}</td>
                  </ng-container>
                  <ng-container matColumnDef="slaStatus">
                    <th mat-header-cell *matHeaderCellDef>SLA</th>
                    <td mat-cell *matCellDef="let t">
                      @if (t.slaStatus === 'Miss') {
                        <span class="sla__status-miss">Miss</span>
                      } @else if (t.slaStatus === 'Met') {
                        <span>Met</span>
                      } @else {
                        <span>—</span>
                      }
                    </td>
                  </ng-container>
                  <ng-container matColumnDef="contactName">
                    <th mat-header-cell *matHeaderCellDef mat-sort-header>Contact</th>
                    <td mat-cell *matCellDef="let t">{{ t.contactName ?? '—' }}</td>
                  </ng-container>
                  <ng-container matColumnDef="summary">
                    <th mat-header-cell *matHeaderCellDef mat-sort-header>Summary</th>
                    <td mat-cell *matCellDef="let t" class="sla__summary-cell">{{ t.summary }}</td>
                  </ng-container>
                  <tr mat-header-row *matHeaderRowDef="expandedTicketsDisplayedColumns"></tr>
                  <tr mat-row *matRowDef="let t; columns: expandedTicketsDisplayedColumns"></tr>
                </table>
                <mat-paginator
                  [length]="expandedTickets?.total ?? 0"
                  [pageSize]="expandedPageSize"
                  [pageIndex]="expandedPage - 1"
                  [pageSizeOptions]="[25, 50, 100]"
                  (page)="onExpandedPageChange($event)"
                ></mat-paginator>
              }
            </div>
          }
        </div>
      }
    </div>
  }
}
